/**
 * HTML Formatting Utilities
 *
 * Pure utility functions for formatting and highlighting HTML
 * used in the scraper preview inspector.
 */

const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

/**
 * Escapes HTML special characters
 */
export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Formats HTML with proper indentation
 */
export const formatHtmlSnippet = (html: string): string => {
  if (!html) {
    return '';
  }

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const lines: string[] = [];

    const formatNode = (node: ChildNode, depth: number) => {
      const indent = '  '.repeat(depth);
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.replace(/\s+/g, ' ').trim();
        if (text) {
          lines.push(`${indent}${text}`);
        }
        return;
      }

      if (node.nodeType === Node.COMMENT_NODE) {
        const text = node.textContent || '';
        lines.push(`${indent}<!--${text}-->`);
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const element = node as Element;
      const tag = element.tagName.toLowerCase();
      const attrs = Array.from(element.attributes)
        .map((attr) => `${attr.name}="${attr.value}"`)
        .join(' ');
      const attrString = attrs ? ` ${attrs}` : '';
      const isVoid = VOID_ELEMENTS.has(tag);

      if (isVoid && element.childNodes.length === 0) {
        lines.push(`${indent}<${tag}${attrString} />`);
        return;
      }

      lines.push(`${indent}<${tag}${attrString}>`);
      element.childNodes.forEach((child) => formatNode(child, depth + 1));
      lines.push(`${indent}</${tag}>`);
    };

    doc.body.childNodes.forEach((node) => formatNode(node, 0));
    return lines.join('\n');
  } catch (error) {
    console.warn('Failed to format HTML snippet', error);
    return html;
  }
};

/**
 * Highlights HTML syntax with color-coded tokens
 */
export const highlightHtmlSnippet = (html: string): string => {
  if (!html) {
    return '';
  }

  let highlighted = escapeHtml(html);

  // Highlight tag names
  highlighted = highlighted.replace(
    /(&lt;\/?)([\w-:]+)/g,
    (_, bracket, name) =>
      `<span class="token punctuation">${bracket}</span><span class="token tag-name">${name}</span>`
  );

  // Highlight attributes
  highlighted = highlighted.replace(
    /(\s+)([\w-:]+)(=)(&quot;.*?&quot;|&#39;.*?&#39;)/g,
    (_, space, attr, equals, value) =>
      `${space}<span class="token attr-name">${attr}</span><span class="token punctuation">${equals}</span><span class="token attr-value">${value}</span>`
  );

  // Highlight closing brackets
  highlighted = highlighted.replace(
    /(&gt;)/g,
    '<span class="token punctuation">$1</span>'
  );

  return highlighted;
};

/**
 * Gets the parent element's HTML as a string
 */
export const getParentHtmlSnippet = (element: Element): string | null => {
  const parent = element.parentElement;
  if (!parent) {
    return null;
  }
  return parent.outerHTML.trim();
};

/**
 * Gets a preview of element's text content (truncated)
 */
export const getTextPreview = (element: Element): string => {
  const text = element.textContent?.replace(/\s+/g, ' ').trim() || '';
  if (!text) {
    return '';
  }
  return text.length > 90 ? `${text.slice(0, 87)}...` : text;
};
